import React, { useCallback, useEffect, useState } from 'react';

/**
 * Toolbar condivisa dei tre editor report (investigativo, bonifiche, consulenza).
 *
 * `useActiveFormats` dice quali formattazioni sono attive al cursore, per
 * evidenziare i bottoni come in Word. Usa `document.queryCommandState`:
 * deprecato come `execCommand`, ma è l'unica API coerente con contentEditable.
 */

export interface ActiveFormats {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    justifyLeft: boolean;
    justifyCenter: boolean;
    justifyRight: boolean;
    justifyFull: boolean;
    insertUnorderedList: boolean;
    insertOrderedList: boolean;
    /** Elenco "triangolo ▶": è un <ul> con list-style-type custom, va distinto
     * dall'elenco puntato normale (entrambi sono <ul>). */
    triangleList: boolean;
}

const EMPTY_FORMATS: ActiveFormats = {
    bold: false, italic: false, underline: false,
    justifyLeft: false, justifyCenter: false, justifyRight: false, justifyFull: false,
    insertUnorderedList: false, insertOrderedList: false, triangleList: false,
};

/** True se la selezione è dentro un <ul> "triangolo" (list-style-type con ▶). */
function isTriangleList(editor: HTMLElement): boolean {
    const sel = window.getSelection();
    let node: Node | null = sel && sel.anchorNode;
    while (node && node !== editor) {
        if (node instanceof HTMLUListElement) {
            const lst = node.style.listStyleType || window.getComputedStyle(node).listStyleType || '';
            return lst.includes('▶');
        }
        node = node.parentNode;
    }
    return false;
}

/** Scala dimensioni testo (pt). "S/M" è la via di mezzo tra S e M. */
export const FONT_SIZES: { label: string; pt: number }[] = [
    { label: 'XS', pt: 8 },
    { label: 'S', pt: 9 },
    { label: 'S/M', pt: 10 },
    { label: 'M', pt: 11 },
    { label: 'L', pt: 13 },
    { label: 'XL', pt: 16 },
    { label: '2XL', pt: 20 },
];

/**
 * Applica una dimensione (pt) al testo.
 *
 * Con una selezione: execCommand('fontSize') accetta solo 1-7, quindi si usa 7
 * come segnaposto (mantenendo selezione e undo nativi) e poi si sostituisce il
 * <font size=7> con uno style in pt.
 *
 * Con il cursore senza selezione execCommand lascerebbe attiva la size 7 per il
 * testo digitato dopo. Si inserisce invece uno <span> con la dimensione e uno
 * zero-width space, mettendoci dentro il cursore; se c'è già uno span vuoto
 * di questo tipo se ne aggiorna solo la dimensione.
 */
export function applyFontSize(editorRef: React.RefObject<HTMLElement | null>, pt: number): void {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();

    const ZWSP = String.fromCharCode(0x200B); // zero-width space
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && sel.getRangeAt(0).collapsed) {
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        const host = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement | null);
        if (host && host.tagName === 'SPAN' && host.textContent === ZWSP && editor.contains(host)) {
            (host as HTMLElement).style.fontSize = `${pt}pt`;
            return;
        }
        const span = document.createElement('span');
        span.style.fontSize = `${pt}pt`;
        const zwsp = document.createTextNode(ZWSP);
        span.appendChild(zwsp);
        range.insertNode(span);
        const r = document.createRange();
        r.setStart(zwsp, 1);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        return;
    }

    document.execCommand('fontSize', false, '7');
    editor.querySelectorAll('font[size="7"]').forEach(el => {
        const f = el as HTMLElement;
        f.removeAttribute('size');
        f.style.fontSize = `${pt}pt`;
    });
}

/** Dimensione (pt) del testo alla selezione, o null se fuori editor. */
function currentFontPt(editor: HTMLElement): number | null {
    const sel = window.getSelection();
    const node = sel && sel.anchorNode;
    const el = node
        ? (node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement))
        : null;
    if (!el || !editor.contains(el)) return null;
    const px = parseFloat(window.getComputedStyle(el).fontSize);
    if (!isFinite(px)) return null;
    return (px * 72) / 96; // px → pt
}

/** Interlinea del blocco al cursore, come rapporto line-height/font-size
 * arrotondato (es. "1", "1.25", "1.35", "1.5"), o null se fuori editor/normal.
 * Serve al controllo Interlinea per mostrare il valore già applicato. */
function currentLineHeight(editor: HTMLElement): string | null {
    const sel = window.getSelection();
    const node = sel && sel.anchorNode;
    let el: HTMLElement | null = node
        ? (node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement))
        : null;
    // Risale al blocco contenitore (stessa lista di tag del controllo Interlinea).
    const BLOCKS = ['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'BLOCKQUOTE'];
    while (el && el !== editor && !BLOCKS.includes(el.tagName)) el = el.parentElement;
    if (!el || el === editor || !editor.contains(el)) return null;
    const cs = window.getComputedStyle(el);
    const fs = parseFloat(cs.fontSize);
    const lh = parseFloat(cs.lineHeight); // px; NaN se "normal"
    if (!isFinite(fs) || !isFinite(lh) || fs <= 0) return null;
    // Arrotonda a 2 decimali e toglie gli zeri finali ("1.50"→"1.5", "1.00"→"1").
    return (Math.round((lh / fs) * 100) / 100).toString();
}

/** Etichetta della scala più vicina alla dimensione corrente (default "M"). */
function ptToLabel(pt: number | null): string {
    if (pt == null) return 'M';
    let best = FONT_SIZES[0];
    let bestD = Infinity;
    for (const s of FONT_SIZES) {
        const d = Math.abs(s.pt - pt);
        if (d < bestD) { bestD = d; best = s; }
    }
    return best.label;
}

const COMMANDS = Object.keys(EMPTY_FORMATS) as (keyof ActiveFormats)[];

/**
 * Ritorna lo stato attivo dei comandi + una `refresh()` da chiamare dopo un
 * execCommand (il toggle non emette selectionchange, va forzato). Ricalcola da
 * solo su ogni selectionchange, ma solo quando la selezione è dentro l'editor.
 */
export function useActiveFormats(
    editorRef: React.RefObject<HTMLElement | null>,
): { formats: ActiveFormats; fontLabel: string; lineHeightLabel: string; refresh: () => void } {
    const [formats, setFormats] = useState<ActiveFormats>(EMPTY_FORMATS);
    // Etichetta della dimensione testo al cursore (per il select controllato).
    const [fontLabel, setFontLabel] = useState<string>('M');
    // Interlinea del blocco al cursore (stringa vuota se non rilevabile).
    const [lineHeightLabel, setLineHeightLabel] = useState<string>('');

    const refresh = useCallback(() => {
        const editor = editorRef.current;
        if (!editor) return;
        const sel = window.getSelection();
        const anchor = sel && sel.anchorNode;
        // Si aggiorna solo se la selezione è dentro questo editor.
        if (!anchor || !editor.contains(anchor)) return;

        let next: ActiveFormats;
        try {
            const inUl = document.queryCommandState('insertUnorderedList');
            next = {
                bold: document.queryCommandState('bold'),
                italic: document.queryCommandState('italic'),
                underline: document.queryCommandState('underline'),
                justifyLeft: document.queryCommandState('justifyLeft'),
                justifyCenter: document.queryCommandState('justifyCenter'),
                justifyRight: document.queryCommandState('justifyRight'),
                justifyFull: document.queryCommandState('justifyFull'),
                insertUnorderedList: inUl,
                insertOrderedList: document.queryCommandState('insertOrderedList'),
                triangleList: inUl ? isTriangleList(editor) : false,
            };
        } catch {
            // queryCommandState può lanciare in edge case (selezione detached).
            return;
        }

        // selectionchange scatta di continuo: evita re-render inutili.
        setFormats(prev => {
            for (const k of COMMANDS) if (next[k] !== prev[k]) return next;
            return prev;
        });
        setFontLabel(ptToLabel(currentFontPt(editor)));
        setLineHeightLabel(currentLineHeight(editor) ?? '');
    }, [editorRef]);

    useEffect(() => {
        const handler = () => refresh();
        document.addEventListener('selectionchange', handler);
        return () => document.removeEventListener('selectionchange', handler);
    }, [refresh]);

    return { formats, fontLabel, lineHeightLabel, refresh };
}

/**
 * Pulsante della toolbar. Con `active` viene evidenziato come premuto;
 * senza, ha lo stile neutro (va bene anche per i bottoni senza stato).
 */
export const ToolbarBtn: React.FC<{
    icon: React.ReactNode;
    onClick: () => void;
    title: string;
    active?: boolean;
}> = ({ icon, onClick, title, active }) => (
    <button
        onMouseDown={e => e.preventDefault()}
        onClick={onClick}
        title={title}
        aria-pressed={active}
        className={`p-1.5 rounded transition-colors ${
            active
                ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300'
                : 'hover:bg-gray-200 text-gray-600'
        }`}
    >
        {icon}
    </button>
);
